import React, { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import {
  Button,
  Chip,
  Link,
  Paper,
  Tab,
  Tabs,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import copyToClipboard from 'copy-to-clipboard';
import { enqueueSnackbar } from 'notistack';
import { useParams } from 'react-router';
import { ExternalLink } from 'react-feather';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUIItem, setUIItem } from '@pv/common/utils';
import { PageLoading } from '@pv/common/components';
import { LoginAsUserButton } from '../components/LoginAsUserButton';
const userQuery = gql`
  query User($id: ID!) {
    user(id: $id) {
      id
      firstName
      lastName
      email
      magicLoginLink
      venues {
        id
        name
      }
      organization {
        id
        name
      }
    }
  }
`;

const VenuesPanel = ({ user }) => {
  const theme = useTheme();
  return (
    <Box sx={{ margin: theme.spacing(2) }}>
      {user.venues.map((v) => (
        <Box key={v.id}>
          <Button variant="contained" href={`/admin/venues/${v.id}`}>
            {v.name}
          </Button>
        </Box>
      ))}
    </Box>
  );
};

const InfoPanel = ({ user }) => {
  const theme = useTheme();

  return (
    <Box sx={{ padding: theme.spacing(2) }}>
      {user.firstName} {user.lastName} ({user.email})
    </Box>
  );
};

const TabPanel = ({ children, value, index }) => {
  if (value !== index) {
    return null;
  }
  return <Box>{children}</Box>;
};

const tabKey = 'user-tab';
const findInitalTab = () => getUIItem(tabKey) || 'info';

export const User = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(findInitalTab());
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = params.id;
  const variables = { id: userId };
  const { data } = useQuery(userQuery, { variables });
  const user = data?.user;

  const onTabChange = (e, t) => {
    navigate(`/admin/users/${user.id}/${t}`);
    setTab(t);
    setUIItem(tabKey, t);
  };

  useEffect(() => {
    const barePathMatch = location.pathname.match(/\/admin\/users\/(\d+)$/);
    if (barePathMatch) {
      navigate(`/admin/users/${barePathMatch[1]}/info`, { replace: true });
      setTab('info');
    } else if (location.pathname.match(/\/admin\/users\/\d+\/info$/)) {
      setTab('info');
    } else if (location.pathname.match(/\/admin\/users\/\d+\/venues/)) {
      setTab('venues');
    }
  }, [location.pathname]);

  if (!user) {
    return <PageLoading />;
  }

  const onClickIdChip = () => {
    copyToClipboard(user.id);
    enqueueSnackbar(`Copied ${user.id} to clipboard`, { variant: 'info' });
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Chip
            clickable
            onClick={onClickIdChip}
            variant="outlined"
            label={`ID ${user.id}`}
          />
          <Typography
            sx={{
              margin: theme.spacing(2),
            }}
            variant="h2"
          >
            User: {user.firstName} {user.lastName}
          </Typography>
        </div>

        <LoginAsUserButton user={user} />
      </div>
      <div style={{ marginBottom: theme.spacing(2) }}>
        <Typography>
          Organization:{' '}
          <Link href={`/admin/organizations/${user?.organization?.id}`}>
            {user.organization?.name}
          </Link>
        </Typography>
      </div>
      <Paper>
        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
          }}
        >
          <div style={{ flexShrink: 1 }}>
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={tab || 'info'}
              onChange={onTabChange}
              sx={{
                borderRight: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Tab value="info" label="Info" />
              <Tab value="venues" label="Venues" />
            </Tabs>
          </div>
          <div style={{ width: '100%', flexGrow: 1, minHeight: 500 }}>
            <TabPanel value={tab} index="info">
              <InfoPanel user={user} />
            </TabPanel>
            <TabPanel value={tab} index="venues">
              <VenuesPanel user={user} />
            </TabPanel>
          </div>
        </Box>
      </Paper>
    </div>
  );
};

export default User;
